import { useState } from 'react'

import AppBar from '@mui/material/AppBar'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Select from '@mui/material/Select'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Toolbar from '@mui/material/Toolbar'
import Typography from '@mui/material/Typography'

import { useAuth } from '../auth/AuthContext'
import {
  useEngineerAccessFailed,
  useEngineerCheckin,
  useEngineerStatus,
  useEngineerToday,
  type EngineerJob,
} from '../hooks/useEngineer'
import { tokens } from '../theme'

// Mobile-first Engineer PWA. Uses the same frontend as the dispatcher
// Control Tower, switched by path. No separate bundle — saves a second
// build + lets the auth flow stay unified.

export default function EngineerPWA() {
  const { user, logout } = useAuth()
  const { data, isLoading } = useEngineerToday()
  const status = useEngineerStatus()
  const checkin = useEngineerCheckin()
  const accessFailed = useEngineerAccessFailed()

  const [accessDialog, setAccessDialog] = useState<{ job: EngineerJob | null }>({ job: null })
  const [category, setCategory] = useState('no_answer')
  const [note, setNote] = useState('')

  const initials = (data?.engineer.display_name ?? 'E')
    .split(/\s+/)
    .map((s) => s[0])
    .slice(0, 2)
    .join('')
    .toUpperCase()

  function handleAccessFailed() {
    if (!accessDialog.job) return
    void accessFailed.mutateAsync({ jobId: accessDialog.job.id, category, note }).then(() => {
      setAccessDialog({ job: null })
      setNote('')
    })
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar position="sticky" elevation={0}>
        <Toolbar sx={{ gap: 1 }}>
          <Avatar
            sx={{
              bgcolor: tokens.gold,
              color: tokens.charcoal,
              width: 32,
              height: 32,
              fontSize: 14,
              fontWeight: 700,
            }}
          >
            {initials}
          </Avatar>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="body2" sx={{ color: 'common.white', lineHeight: 1.1, fontWeight: 600 }}>
              {data?.engineer.display_name ?? 'Engineer'}
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
              {data?.engineer.external_ref} · {user?.tenant_slug}
            </Typography>
          </Box>
          <Button
            variant="text"
            onClick={logout}
            sx={{ color: 'rgba(255,255,255,0.85)', textTransform: 'none', minWidth: 0 }}
            size="small"
          >
            Out
          </Button>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 2, maxWidth: 520, mx: 'auto' }}>
        <Typography variant="overline" sx={{ color: 'text.secondary' }}>
          Today · {data?.jobs.length ?? 0} jobs
        </Typography>

        {isLoading && (
          <Stack sx={{ alignItems: 'center', py: 6 }}>
            <CircularProgress size={24} />
          </Stack>
        )}

        <Stack spacing={1.5} sx={{ mt: 1 }}>
          {(data?.jobs ?? []).length === 0 && !isLoading && (
            <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                No jobs today.
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Check back later — dispatch may still be allocating.
              </Typography>
            </Paper>
          )}

          {(data?.jobs ?? []).map((job) => (
            <JobCard
              key={job.id}
              job={job}
              onEnroute={() =>
                status.mutate({ jobId: job.id, from: job.status, to: 'enroute' })
              }
              onArrival={() =>
                status.mutate({ jobId: job.id, from: job.status, to: 'onsite' })
              }
              onComplete={() =>
                status.mutate({ jobId: job.id, from: job.status, to: 'complete' })
              }
              onCheckin={() => checkin.mutate(job.id)}
              onAccessFailed={() => setAccessDialog({ job })}
              busy={status.isPending || checkin.isPending || accessFailed.isPending}
            />
          ))}
        </Stack>
      </Box>

      <Dialog open={!!accessDialog.job} onClose={() => setAccessDialog({ job: null })} fullWidth maxWidth="xs">
        <DialogTitle>No access — {accessDialog.job?.external_ref}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Select value={category} onChange={(e) => setCategory(e.target.value)} size="small" fullWidth>
              <MenuItem value="no_answer">No answer</MenuItem>
              <MenuItem value="gated">Gated</MenuItem>
              <MenuItem value="wrong_address">Wrong address</MenuItem>
              <MenuItem value="refused">Refused</MenuItem>
              <MenuItem value="pet">Pet on premises</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
            <TextField
              label="Note"
              multiline
              minRows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              size="small"
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAccessDialog({ job: null })}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleAccessFailed}>
            Record
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

function JobCard({
  job,
  onEnroute,
  onArrival,
  onComplete,
  onCheckin,
  onAccessFailed,
  busy,
}: {
  job: EngineerJob
  onEnroute: () => void
  onArrival: () => void
  onComplete: () => void
  onCheckin: () => void
  onAccessFailed: () => void
  busy: boolean
}) {
  const sla = new Date(job.sla_resolve_by)
  const minsToSLA = Math.round((sla.getTime() - Date.now()) / 60000)
  const slaColor = minsToSLA <= 15 ? 'error' : minsToSLA <= 30 ? 'warning' : 'default'

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
          {job.external_ref}
        </Typography>
        <Chip label={job.job_type_name} size="small" variant="outlined" />
        <Box sx={{ flexGrow: 1 }} />
        <Chip
          label={job.priority}
          size="small"
          color={job.priority === 'emergency' ? 'error' : job.priority === 'urgent' ? 'warning' : 'default'}
        />
      </Stack>

      <Typography variant="body2" sx={{ mb: 0.5 }}>
        {job.address_line_1}
      </Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        {job.postcode} · Scheduled {new Date(job.scheduled_start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
      </Typography>
      <Box sx={{ mt: 1, mb: 1.5 }}>
        <Chip size="small" label={`SLA in ${minsToSLA}m`} color={slaColor} />
        <Chip size="small" label={job.status} sx={{ ml: 1, textTransform: 'uppercase', fontSize: 10 }} />
      </Box>

      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
        {job.status === 'scheduled' && (
          <Button size="small" variant="contained" color="secondary" onClick={onEnroute} disabled={busy}>
            Start → En route
          </Button>
        )}
        {job.status === 'enroute' && (
          <>
            <Button size="small" variant="contained" color="secondary" onClick={onArrival} disabled={busy}>
              Arrived
            </Button>
            <Button size="small" variant="outlined" color="error" onClick={onAccessFailed} disabled={busy}>
              No access
            </Button>
          </>
        )}
        {job.status === 'onsite' && (
          <>
            <Button size="small" variant="contained" color="secondary" onClick={onComplete} disabled={busy}>
              Complete
            </Button>
            <Button size="small" variant="outlined" onClick={onCheckin} disabled={busy}>
              Check-in
            </Button>
          </>
        )}
        {job.status === 'complete' && (
          <Chip label="Complete" color="success" size="small" />
        )}
      </Stack>
    </Paper>
  )
}
